'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { Button } from '@/components/atoms';

interface TeamLeagueDetailHeaderNavButtonsProps {
	teamLeagueId: string;
	hasPermission?: boolean;
	shouldHideDistrics?: boolean;
	shouldHideDivisions?: boolean;
	isSeasonActive: boolean;
	isRegistrationOpen: boolean | null;
	isSeasonBtnVisible?: boolean;
}

interface NavItem {
	label: string;
	href: string;
}

export const generateNavItems = (
	isSeasonActive: boolean,
	shouldHideDistrics?: boolean,
	shouldHideDivisions?: boolean,
	isRegistrationOpen?: boolean | null,
	isSeasonBtnVisible?: boolean
): NavItem[] => {
	if (!isRegistrationOpen) {
		return [
			{ label: 'Standings', href: '/standings' },
			{ label: 'Schedule', href: '/gameday' },
			...(!shouldHideDivisions ? [{ label: 'Divisions', href: '/divisions' }] : []),
			{ label: 'Teams', href: '/teams' },
			{ label: 'Players', href: '/players' },
			...(!shouldHideDistrics ? [{ label: 'Districts', href: '/districts' }] : []),
			{ label: 'Details', href: '/' },
			...(isSeasonBtnVisible ? [{ label: 'Sponsors', href: '/sponsors' }] : [])
		];
	}
	return [
		isSeasonActive ? { label: 'Details', href: '/' } : { label: 'Standings', href: '/standings' },
		{ label: 'Schedule', href: '/gameday' },
		...(!shouldHideDivisions ? [{ label: 'Divisions', href: '/divisions' }] : []),
		{ label: 'Teams', href: '/teams' },
		{ label: 'Players', href: '/players' },
		...(!shouldHideDistrics ? [{ label: 'Districts', href: '/districts' }] : []),
		...(isSeasonBtnVisible ? [{ label: 'Sponsors', href: '/sponsors' }] : [])
	];
};

export const TeamLeagueDetailHeaderNavButtons = ({
	teamLeagueId,
	shouldHideDistrics,
	shouldHideDivisions,
	isSeasonActive,
	isRegistrationOpen,
	isSeasonBtnVisible = false
}: TeamLeagueDetailHeaderNavButtonsProps) => {
	const currentPath = usePathname();
	const segmentedCurrentPath = currentPath.split('/');
	const filteredNavItems = generateNavItems(isSeasonActive, shouldHideDistrics, shouldHideDivisions, isRegistrationOpen, isSeasonBtnVisible);

	return (
		<div className="flex w-full justify-center bg-gray-soft py-4">
			<div className="no-scrollbar flex gap-5 overflow-x-auto px-4 sm:px-8">
				{filteredNavItems.map(({ label, href }, index) => (
					<Link href={`/team-leagues/${teamLeagueId}${href}`} key={`${label}_${index}`} className="text-nowrap">
						<Button
							className={`flex w-full flex-1 border !border-gray-300 px-4 py-2 font-semibold text-gray-800  ${
								(href !== '/' && currentPath.includes(href)) || (href === '/' && segmentedCurrentPath.length === 3)
									? 'bg-orange-500 text-white hover:bg-orange-700'
									: 'bg-white'
							}`}
						>
							{label}
						</Button>
					</Link>
				))}
			</div>
		</div>
	);
};
